import { AuthService, ApiResponse, SocialLoginResponse } from '../types';
import {
  UserSignup,
  UserLogin,
  AuthResponse,
  UserResponse,
  SocialLoginRequest,
  SocialLoginCallback,
  PasswordResetRequest,
  PasswordReset,
} from '../../model';
import { BaseService } from '../base-service';

export class AuthServiceReal extends BaseService implements AuthService {
  constructor() {
    super('AuthService');
  }

  // Override makeRequest for auth-specific needs (some endpoints don't need auth)
  private async makeAuthRequest<T>(
    endpoint: string,
    options?: RequestInit,
    requireAuth = true
  ): Promise<ApiResponse<T>> {
    if (requireAuth) {
      return this.makeRequest<T>(endpoint, options);
    } else {
      // For endpoints that don't need auth (like login, signup)
      try {
        const url = `${this.baseUrl}${endpoint}`;
        this.log('makeAuthRequest', { url, options });

        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
          },
          ...options,
        });

        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || errorMessage;
          } catch {
            // If response is not JSON, use the default error message
          }
          throw new Error(errorMessage);
        }

        const data = await response.json();

        return {
          data: data,
          success: true,
          message: data.message || 'Request successful',
        };
      } catch (error) {
        console.error('Auth API Request failed:', error);
        return {
          data: null as any,
          success: false,
          message: error instanceof Error ? error.message : 'Unknown error occurred',
        };
      }
    }
  }

  async signup(userData: UserSignup): Promise<ApiResponse<AuthResponse>> {
    this.log('signup', { email: userData.email });
    return this.makeAuthRequest<AuthResponse>(
      '/auth/signup',
      {
        method: 'POST',
        body: JSON.stringify(userData),
      },
      false
    ); // No auth required for signup
  }

  async login(userData: UserLogin): Promise<ApiResponse<AuthResponse>> {
    this.log('login', { email: userData.email });
    return this.makeAuthRequest<AuthResponse>(
      '/auth/login',
      {
        method: 'POST',
        body: JSON.stringify(userData),
      },
      false
    ); // No auth required for login
  }

  async logout(): Promise<ApiResponse<{ message: string }>> {
    this.log('logout');
    return this.makeAuthRequest<{ message: string }>(
      '/auth/logout',
      {
        method: 'POST',
      },
      true
    ); // Auth required for logout
  }

  async refreshToken(refreshToken: string): Promise<ApiResponse<AuthResponse>> {
    this.log('refreshToken');
    return this.makeAuthRequest<AuthResponse>(
      '/auth/refresh',
      {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken }),
      },
      false
    ); // No auth required for refresh
  }

  async getCurrentUser(): Promise<ApiResponse<UserResponse>> {
    this.log('getCurrentUser');
    return this.makeAuthRequest<UserResponse>('/auth/me', {}, true); // Auth required
  }

  async initiateSocialLogin(
    request: SocialLoginRequest
  ): Promise<ApiResponse<SocialLoginResponse>> {
    this.log('initiateSocialLogin', { provider: request.provider });
    return this.makeAuthRequest<SocialLoginResponse>(
      '/auth/social/login',
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
      false
    ); // No auth required for social login initiation
  }

  async handleSocialCallback(request: SocialLoginCallback): Promise<ApiResponse<AuthResponse>> {
    this.log('handleSocialCallback');
    return this.makeAuthRequest<AuthResponse>(
      '/auth/social/callback',
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
      false
    ); // No auth required for callback
  }

  async requestPasswordReset(
    request: PasswordResetRequest
  ): Promise<ApiResponse<{ message: string }>> {
    this.log('requestPasswordReset', { email: request.email });
    return this.makeAuthRequest<{ message: string }>(
      '/auth/reset-password',
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
      false
    ); // No auth required for password reset request
  }

  async confirmPasswordReset(request: PasswordReset): Promise<ApiResponse<{ message: string }>> {
    this.log('confirmPasswordReset');
    return this.makeAuthRequest<{ message: string }>(
      '/auth/reset-password/confirm',
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
      false
    ); // No auth required for password reset confirmation
  }
}
